import { useState, useRef, useEffect, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAmountDown } from '@fortawesome/free-solid-svg-icons';
import './Table.css';

/**
 * Reusable Table Component
 *
 * @param {Array} columns - Array of column definitions
 *   Example: [
 *     { key: 'id', label: 'ID', width: '80px' },
 *     { key: 'name', label: 'Name', render: (value, row) => <strong>{value}</strong> },
 *     { key: 'description', label: 'Description', truncate: true } // Auto-truncate long text with "..."
 *   ]
 *   Note:
 *   - If column has `render` function, truncation is not applied (assumes custom rendering)
 *   - Set `truncate: false` to disable auto-truncation for a column
 *   - Tooltip is shown automatically on hover for all text values when truncation is enabled
 *   - Set `enableTooltip: false` on a column to disable tooltip for that column
 *   - Each column automatically gets a default max width (override via column.maxWidth or table prop)
 * @param {Array} data - Array of data objects
 * @param {Boolean} selectable - Enable row selection with checkboxes
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Array} selectedIds - Array of selected row IDs (controlled)
 * @param {Function} isRowSelectable - Function to determine if a row can be selected: (row) => boolean
 * @param {Function} onEdit - Callback for edit button: (row) => void
 * @param {Function} onDelete - Callback for delete button: (row) => void
 * @param {Function} isRowDeletable - Function to determine if a row can be deleted: (row) => boolean
 * @param {Function} renderActions - Custom function to render action buttons per row (overrides onEdit/onDelete)
 * @param {Function} onRowDoubleClick - Callback for double-clicking a row: (row) => void
 * @param {Boolean} loading - Show loading state
 * @param {String} emptyMessage - Message to show when no data
 * @param {String} idField - Field name to use as unique identifier (default: 'id')
 * @param {Boolean} pagination - Enable pagination (default: false)
 * @param {Number} page - Current page (1-indexed, controlled)
 * @param {Number} pageSize - Items per page (controlled)
 * @param {Number} totalItems - Total number of items (for controlled pagination)
 * @param {Function} onPageChange - Callback when page changes: (newPage) => void
 * @param {Function} onPageSizeChange - Callback when page size changes: (newPageSize) => void
 * @param {Array} pageSizeOptions - Available page size options (default: [10, 20, 50, 100])
 * @param {Boolean} sortable - Enable column sorting (default: false)
 * @param {String} sortColumn - Currently sorted column key (controlled)
 * @param {String} sortDirection - Sort direction: 'asc' or 'desc' (controlled)
 * @param {Function} onSortChange - Callback when sort changes: (column, direction) => void
 * @param {Array} sortableColumns - Array of column keys that can be sorted (default: all columns)
 * @param {String} defaultColumnMaxWidth - Default max width applied to columns without explicit maxWidth (default: 300px)
 */
function Table({
    id = 'table',
    columns = [],
    data = [],
    selectable = false,
    onSelectionChange,
    selectedIds = [],
    isRowSelectable,
    onEdit,
    onDelete,
    isRowDeletable,
    renderActions,
    onRowDoubleClick,
    loading = false,
    emptyMessage,
    idField = 'id',
    className = '',
    pagination = false,
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
    sortable = false,
    sortColumn,
    sortDirection,
    onSortChange,
    sortableColumns = [],
}) {
    const { t } = useTranslation();
    const [internalSelectedIds, setInternalSelectedIds] = useState([]);
    const [internalPage, setInternalPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(pageSizeOptions[0]);
    const [internalSortColumn, setInternalSortColumn] = useState('');
    const [internalSortDirection, setInternalSortDirection] = useState('asc');

    // Refs for horizontal scroll sync
    const headerContainerRef = useRef(null);
    const bodyContainerRef = useRef(null);
    const headerTableRef = useRef(null);
    const bodyTableRef = useRef(null);
    const [columnWidths, setColumnWidths] = useState([]);

    // Sync horizontal scroll between header and body
    useEffect(() => {
        const bodyEl = bodyContainerRef.current;
        const headerEl = headerContainerRef.current;

        if (!bodyEl || !headerEl) return;

        const handleBodyScroll = () => {
            headerEl.scrollLeft = bodyEl.scrollLeft;
        };

        bodyEl.addEventListener('scroll', handleBodyScroll);
        return () => bodyEl.removeEventListener('scroll', handleBodyScroll);
    }, []);

    // No dynamic width syncing; all columns use fixed widths

    // Use controlled or uncontrolled selection
    const selected = onSelectionChange ? selectedIds : internalSelectedIds;
    const setSelected = onSelectionChange || setInternalSelectedIds;

    // Use controlled or uncontrolled pagination
    const currentPage = pagination && onPageChange ? page : internalPage;
    const currentPageSize = pagination && onPageSizeChange ? pageSize : internalPageSize;
    const setCurrentPage = onPageChange || setInternalPage;
    const setCurrentPageSize = onPageSizeChange || setInternalPageSize;

    // Use controlled or uncontrolled sort
    const currentSortColumn = sortable && onSortChange ? sortColumn : internalSortColumn;
    const currentSortDirection = sortable && onSortChange ? sortDirection : internalSortDirection;
    const setSort = (column, direction) => {
        if (onSortChange) {
            onSortChange(column, direction);
        } else {
            setInternalSortColumn(column);
            setInternalSortDirection(direction);
        }
    };

    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];

    // Calculate pagination
    const total = totalItems !== undefined ? totalItems : dataArray.length;
    const totalPages = Math.ceil(total / currentPageSize);

    // Paginate data (only for client-side pagination)
    const paginatedData = pagination && !totalItems ? dataArray.slice((currentPage - 1) * currentPageSize, currentPage * currentPageSize) : dataArray;

    // Handle select all (only for current page if paginated)
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const selectableRows = isRowSelectable ? paginatedData.filter((row) => isRowSelectable(row)) : paginatedData;
            const allIds = selectableRows.map((row) => row[idField]);
            setSelected(allIds);
        } else {
            setSelected([]);
        }
    };

    // Handle select one
    const handleSelectOne = (rowId) => {
        if (selected.includes(rowId)) {
            setSelected(selected.filter((id) => id !== rowId));
        } else {
            setSelected([...selected, rowId]);
        }
    };

    // Check if all selectable rows on current page are selected
    const selectableRowsOnPage = isRowSelectable ? paginatedData.filter((row) => isRowSelectable(row)) : paginatedData;
    const isAllSelected = selectableRowsOnPage.length > 0 && selectableRowsOnPage.every((row) => selected.includes(row[idField]));

    const columnMaxWidthValue = '300px';
    const getColumnMaxWidth = (column) => {
        if (column?.maxWidth === undefined) return columnMaxWidthValue;
        if (column.maxWidth === null) return 'none';
        return typeof column.maxWidth === 'number' ? `${column.maxWidth}px` : column.maxWidth;
    };
    const getColumnWidth = (column) => {
        if (!column || column.width === undefined) return undefined;
        if (column.width === null) return null;
        return typeof column.width === 'number' ? `${column.width}px` : column.width;
    };
    useEffect(() => {
        const bodyTable = bodyTableRef.current;
        if (!bodyTable) return;
        const firstRow = bodyTable.querySelector('tbody tr:not(.table-loading-row):not(.table-empty-row)');
        if (!firstRow) return;

        const cells = Array.from(firstRow.querySelectorAll('td'));
        const offset = selectable ? 1 : 0;
        const measured = columns.map((column, index) => {
            const configuredWidth = getColumnWidth(column);
            if (configuredWidth !== undefined && configuredWidth !== null) {
                const numeric = parseInt(configuredWidth, 10);
                return Number.isNaN(numeric) ? undefined : numeric;
            }
            const cell = cells[index + offset];
            if (!cell) return undefined;
            const width = cell.getBoundingClientRect().width;
            const maxWidthValue = getColumnMaxWidth(column);
            const numericMax = maxWidthValue && maxWidthValue !== 'none' ? parseInt(maxWidthValue, 10) : undefined;
            if (numericMax && !Number.isNaN(numericMax)) {
                return Math.min(Math.ceil(width), numericMax);
            }
            return Math.ceil(width);
        });
        setColumnWidths(measured);
    }, [columns, paginatedData, loading, selectable]);

    // Render cell content
    const renderCell = (column, row, columnMaxWidth) => {
        const originalValue = row[column.key];
        const renderedValue = column.render ? column.render(originalValue, row) : originalValue;

        if (isValidElement(renderedValue)) {
            // Wrap React elements in a container to enforce width constraints
            return (
                <div
                    style={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                    }}
                >
                    {renderedValue}
                </div>
            );
        }

        const textValue = renderedValue === null || renderedValue === undefined ? '' : String(renderedValue);
        const isStringValue = textValue !== '';
        const isTruncationEnabled = column.truncate !== false;
        // Always show tooltip for non-empty string values when truncation is enabled
        // This ensures tooltip shows even when CSS truncates text visually
        const shouldShowTooltip = column.enableTooltip !== false && isStringValue && isTruncationEnabled;

        if (!isStringValue) {
            return renderedValue;
        }

        const textContent = (
            <span
                style={{
                    display: 'block',
                    maxWidth: columnMaxWidth || '100%',
                    overflow: isTruncationEnabled ? 'hidden' : 'visible',
                    textOverflow: isTruncationEnabled ? 'ellipsis' : 'unset',
                    whiteSpace: isTruncationEnabled ? 'nowrap' : 'normal',
                }}
            >
                {textValue}
            </span>
        );

        if (!shouldShowTooltip) {
            return textContent;
        }

        return (
            <Tooltip title={textValue} arrow placement={column.tooltipPlacement || 'top'}>
                {textContent}
            </Tooltip>
        );
    };

    // Create default actions renderer if onEdit or onDelete are provided
    const defaultActionsRenderer = (row) => {
        const isDeletable = isRowDeletable ? isRowDeletable(row) : true;

        return (
            <>
                {onEdit && (
                    <IconButton
                        onClick={() => onEdit(row)}
                        title={t('table.actions.edit')}
                        color="primary"
                        size="small"
                        id={`${id}-button-edit-${row[idField]}`}
                        name={`${id}-button-edit-${row[idField]}`}
                        data-testid={`${id}-button-edit-${row[idField]}`}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
                {onDelete && (
                    <IconButton
                        onClick={() => onDelete(row)}
                        title={t('table.actions.delete')}
                        color="error"
                        size="small"
                        disabled={!isDeletable}
                        id={`${id}-button-delete-${row[idField]}`}
                        name={`${id}-button-delete-${row[idField]}`}
                        data-testid={`${id}-button-delete-${row[idField]}`}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </>
        );
    };

    // Use custom renderActions if provided, otherwise use default if onEdit/onDelete exist
    const actionsRenderer = renderActions || (onEdit || onDelete ? defaultActionsRenderer : null);

    return (
        <div className={`table-wrapper ${className}`} data-testid={id}>
            <div className="table-header-container" ref={headerContainerRef}>
                <table className="reusable-table" ref={headerTableRef}>
                    <colgroup>
                        {selectable && <col style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }} />}
                        {columns.map((column, index) => {
                            const columnMaxWidth = getColumnMaxWidth(column);
                            const configuredWidth = getColumnWidth(column);
                            const measuredWidth = columnWidths[index];
                            return (
                                <col
                                    key={column.key}
                                    style={{
                                        width: configuredWidth ?? (measuredWidth ? `${measuredWidth}px` : undefined),
                                        maxWidth: columnMaxWidth,
                                    }}
                                />
                            );
                        })}
                        {actionsRenderer && <col style={{ width: '200px', minWidth: '200px' }} />}
                    </colgroup>
                    <thead>
                        <tr>
                            {selectable && (
                                <th style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }} onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleSelectAll(e);
                                        }}
                                        className="table-checkbox"
                                        id={`${id}-checkbox-selectAll`}
                                        name={`${id}-checkbox-selectAll`}
                                        data-testid={`${id}-checkbox-selectAll`}
                                    />
                                </th>
                            )}
                            {columns.map((column, index) => {
                                const columnMaxWidth = getColumnMaxWidth(column);
                                return (
                                    <th
                                        key={column.key}
                                        style={{
                                            maxWidth: columnMaxWidth,
                                            width: columnWidths[index] ? `${columnWidths[index]}px` : undefined,
                                            ...(column.align ? { textAlign: column.align } : {}),
                                        }}
                                        className={column.headerClassName || ''}
                                    >
                                        <Tooltip title={column.label} arrow placement="top">
                                            <span
                                                style={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {column.label}
                                            </span>
                                        </Tooltip>
                                    </th>
                                );
                            })}
                            {actionsRenderer && <th style={{ width: '200px', minWidth: '200px' }}>{t('table.actions')}</th>}
                        </tr>
                    </thead>
                </table>
            </div>
            <div className="table-body-container" ref={bodyContainerRef}>
                <table className="reusable-table" ref={bodyTableRef}>
                    <colgroup>
                        {selectable && <col style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }} />}
                        {columns.map((column, index) => {
                            const columnMaxWidth = getColumnMaxWidth(column);
                            const configuredWidth = getColumnWidth(column);
                            const measuredWidth = columnWidths[index];
                            return (
                                <col
                                    key={column.key}
                                    style={{
                                        width: configuredWidth ?? (measuredWidth ? `${measuredWidth}px` : undefined),
                                        maxWidth: columnMaxWidth,
                                    }}
                                />
                            );
                        })}
                        {actionsRenderer && <col style={{ width: '200px', minWidth: '200px' }} />}
                    </colgroup>
                    <tbody>
                        {loading ? (
                            <tr className="table-loading-row">
                                <td colSpan={columns.length + (selectable ? 1 : 0) + (actionsRenderer ? 1 : 0)} className="table-loading">
                                    <CircularProgress size={24} sx={{ mr: 1 }} />
                                    {t('table.loading')}
                                </td>
                            </tr>
                        ) : paginatedData.length === 0 ? (
                            <tr className="table-empty-row">
                                <td colSpan={columns.length + (selectable ? 1 : 0) + (actionsRenderer ? 1 : 0)} className="table-empty">
                                    {emptyMessage || t('table.noData')}
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row) => {
                                const isSelectable = isRowSelectable ? isRowSelectable(row) : true;

                                // Handle row click for selection
                                const handleRowClick = (e) => {
                                    // Only trigger selection if:
                                    // 1. Row is selectable
                                    // 2. Table has selectable prop enabled
                                    // 3. Click is not on an interactive element (checkbox, button, input, select, link)
                                    if (!selectable || !isSelectable) return;

                                    const target = e.target;
                                    const isInteractive =
                                        target.tagName === 'INPUT' ||
                                        target.tagName === 'BUTTON' ||
                                        target.tagName === 'A' ||
                                        target.tagName === 'SELECT' ||
                                        target.closest('button') ||
                                        target.closest('a');

                                    if (!isInteractive) {
                                        handleSelectOne(row[idField]);
                                    }
                                };

                                // Handle row double-click
                                const handleRowDoubleClick = (e) => {
                                    // Only trigger double-click if callback is provided
                                    if (!onRowDoubleClick) return;

                                    const target = e.target;
                                    const isInteractive =
                                        target.tagName === 'INPUT' ||
                                        target.tagName === 'BUTTON' ||
                                        target.tagName === 'A' ||
                                        target.tagName === 'SELECT' ||
                                        target.closest('button') ||
                                        target.closest('a') ||
                                        target.closest('.table-checkbox');

                                    if (!isInteractive) {
                                        onRowDoubleClick(row);
                                    }
                                };

                                return (
                                    <tr
                                        key={row[idField]}
                                        onClick={handleRowClick}
                                        onDoubleClick={handleRowDoubleClick}
                                        className={selectable && isSelectable ? 'selectable-row' : ''}
                                        style={{ cursor: onRowDoubleClick ? 'pointer' : 'default' }}
                                    >
                                        {selectable && (
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(row[idField])}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectOne(row[idField]);
                                                    }}
                                                    disabled={!isSelectable}
                                                    className="table-checkbox"
                                                    id={`${id}-checkbox-row-${row[idField]}`}
                                                    name={`${id}-checkbox-row-${row[idField]}`}
                                                    data-testid={`${id}-checkbox-row-${row[idField]}`}
                                                />
                                            </td>
                                        )}
                                        {columns.map((column, index) => {
                                            const columnMaxWidth = getColumnMaxWidth(column);
                                            const configuredWidth = getColumnWidth(column);
                                            return (
                                                <td
                                                    key={column.key}
                                                    className={column.cellClassName || ''}
                                                    style={{
                                                        maxWidth: columnMaxWidth,
                                                        width: configuredWidth ?? (columnWidths[index] ? `${columnWidths[index]}px` : undefined),
                                                        ...(column.align ? { textAlign: column.align } : {}),
                                                    }}
                                                >
                                                    {renderCell(column, row, columnMaxWidth)}
                                                </td>
                                            );
                                        })}
                                        {actionsRenderer && <td className="table-actions">{actionsRenderer(row)}</td>}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {pagination && total > 0 && (
                <div className="table-pagination">
                    <div className="pagination-left">
                        {sortable && (
                            <div className="pagination-sort">
                                <label>{t('table.sort.column')}</label>
                                <div>
                                    <select
                                        className="sort-select"
                                        value={currentSortColumn}
                                        onChange={(e) => setSort(e.target.value, currentSortDirection)}
                                        id={`${id}-select-sortColumn`}
                                        name={`${id}-select-sortColumn`}
                                        data-testid={`${id}-select-sortColumn`}
                                    >
                                        {(sortableColumns.length > 0 ? columns.filter((col) => sortableColumns.includes(col.key)) : columns).map(
                                            (column) => (
                                                <option key={column.key} value={column.key}>
                                                    {column.label}
                                                </option>
                                            )
                                        )}
                                    </select>
                                    <IconButton
                                        className="sort-direction-btn"
                                        onClick={() => setSort(currentSortColumn, currentSortDirection === 'asc' ? 'desc' : 'asc')}
                                        title={currentSortDirection === 'asc' ? t('table.sort.ascending') : t('table.sort.descending')}
                                        size="small"
                                        id={`${id}-button-sortDirection`}
                                        name={`${id}-button-sortDirection`}
                                        data-testid={`${id}-button-sortDirection`}
                                    >
                                        <span
                                            style={
                                                currentSortDirection === 'asc'
                                                    ? { transform: 'rotateX(180deg)', display: 'inline-block' }
                                                    : { display: 'inline-block' }
                                            }
                                        >
                                            <FontAwesomeIcon icon={faSortAmountDown} />
                                        </span>
                                    </IconButton>
                                </div>
                            </div>
                        )}
                        {selectable && selected.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '14px',
                                    color: '#374151',
                                    fontWeight: '500',
                                    marginLeft: sortable ? '16px' : '0',
                                }}
                            >
                                <span>
                                    {t(selected.length === 1 ? 'table.selected.items' : 'table.selected.items_plural', { count: selected.length })}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="pagination-right">
                        <div className="pagination-pagesize">
                            <label>{t('table.pagination.perPage')}</label>
                            <select
                                className="pagesize-select"
                                value={currentPageSize}
                                onChange={(e) => {
                                    setCurrentPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                id={`${id}-select-pageSize`}
                                name={`${id}-select-pageSize`}
                                data-testid={`${id}-select-pageSize`}
                            >
                                {pageSizeOptions.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="pagination-controls">
                            <IconButton
                                className="pagination-btn"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                size="small"
                                id={`${id}-button-firstPage`}
                                name={`${id}-button-firstPage`}
                                data-testid={`${id}-button-firstPage`}
                            >
                                <FirstPageIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                className="pagination-btn"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                size="small"
                                id={`${id}-button-prevPage`}
                                name={`${id}-button-prevPage`}
                                data-testid={`${id}-button-prevPage`}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <span className="page-indicator">
                                {currentPage} / {totalPages}
                            </span>
                            <IconButton
                                className="pagination-btn"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                size="small"
                                id={`${id}-button-nextPage`}
                                name={`${id}-button-nextPage`}
                                data-testid={`${id}-button-nextPage`}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                className="pagination-btn"
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                size="small"
                                id={`${id}-button-lastPage`}
                                name={`${id}-button-lastPage`}
                                data-testid={`${id}-button-lastPage`}
                            >
                                <LastPageIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

Table.propTypes = {
    id: PropTypes.string,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            width: PropTypes.string,
            align: PropTypes.oneOf(['left', 'center', 'right']),
            render: PropTypes.func,
            truncate: PropTypes.bool,
            truncateThreshold: PropTypes.number,
            enableTooltip: PropTypes.bool,
            tooltipPlacement: PropTypes.string,
            headerClassName: PropTypes.string,
            cellClassName: PropTypes.string,
        })
    ).isRequired,
    data: PropTypes.array.isRequired,
    selectable: PropTypes.bool,
    onSelectionChange: PropTypes.func,
    selectedIds: PropTypes.array,
    isRowSelectable: PropTypes.func,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    isRowDeletable: PropTypes.func,
    renderActions: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    loading: PropTypes.bool,
    emptyMessage: PropTypes.string,
    idField: PropTypes.string,
    className: PropTypes.string,
    pagination: PropTypes.bool,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalItems: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    sortable: PropTypes.bool,
    sortColumn: PropTypes.string,
    sortDirection: PropTypes.oneOf(['asc', 'desc']),
    onSortChange: PropTypes.func,
    sortableColumns: PropTypes.arrayOf(PropTypes.string),
};

export default Table;
